import {
  Checkbox,
  Grid,
  Icon,
  Input,
  Loader,
  Popup,
  Ref,
} from "semantic-ui-react";
import FancyHeader from "../../../../components/shared/FancyHeader";
import useGetCurrentNetwork from "./hooks/useGetCurrentNetwork";
import { ValidOpenstackResource } from "../../../types";
import Fallback from "../../../../components/slidingpanel/Fallback";
import { getZoneidFromRegionName } from "../../../../app_shared_functions";
import { useSelector } from "react-redux";
import { getDomains } from "../../../../selectors/projects";
import useNetworkModifyForm from "./hooks/useNetworkModifyForm";
import { useRef } from "react";
import Expandable from "../../../../components/shared/Expandable";
import MTUStatus from "../create/bits/MTUStatus";
import useCheckFormChanged from "./hooks/useCheckFormChanged";
import { formFieldWarningClassName } from "../../../../shared-functions/form";
import useModifyClickErrorHandler from "./hooks/useModifyClickErrorHandler";
import useNetworkModifyAction from "./hooks/useNetworkModifyAction";
import ConnectedSubnets from "./subnets/ConnectedSubnets";
import ConnectedPorts from "./ports/ConnectedPorts";

type ModifyNetworkProps = {
  network: ValidOpenstackResource;
  closeSlidingMenuLayer: () => void;
};

const ModifyNetwork = ({
  network,
  closeSlidingMenuLayer,
}: ModifyNetworkProps) => {
  // Fetch the full network object from Redux, since the one passed here may be incomplete.
  const { selectedNetwork, isLoadingNetworks } = useGetCurrentNetwork(
    network.id,
  );

  const nameRef = useRef<HTMLElement | null>(null);

  const domains = useSelector(getDomains);

  const zoneId = getZoneidFromRegionName(network.region, domains);

  const {
    error,
    formData,
    handleInputChange,
    handleCheckboxChange,
    formWarning,
    showFormWarning,
  } = useNetworkModifyForm({
    nameRef,
    network: selectedNetwork,
  });

  const { modifyAction, isModifying } = useNetworkModifyAction({
    network: selectedNetwork,
    formData,
  });

  const isFormChanged = useCheckFormChanged(formData, selectedNetwork);

  const onDisabledClick = useModifyClickErrorHandler({
    error,
    showFormWarning,
  });

  if (!selectedNetwork) {
    if (isLoadingNetworks) {
      return (
        <div className="creator-component-wrapper">
          <FancyHeader title="Modify Network" knowledgeBase />
          <p />
          <Grid>
            <Loader size="mini" active>
              Fetching network details...
            </Loader>
          </Grid>
        </div>
      );
    }
    return <Fallback component="Network" />;
  }

  return (
    <div className="creator-component-wrapper">
      <div>
        <FancyHeader
          title="Modify Network"
          subtitle={network.name}
          region={network.region}
        />

        <p />

        <Grid>
          {/* NAME */}
          <Grid.Row className="separator animateDown">
            <Grid.Column textAlign="left" width={8} className="flex vcenter ">
              <h5>Name</h5>
            </Grid.Column>
            <Grid.Column textAlign="left" width={8}>
              <Ref innerRef={nameRef}>
                <Input
                  value={formData.name}
                  name="name"
                  className={`select-box full ${formFieldWarningClassName(
                    formWarning,
                    error?.ref,
                    nameRef,
                  )}`}
                  onChange={handleInputChange}
                />
              </Ref>
            </Grid.Column>
          </Grid.Row>

          {/* ADVANCED */}
          <>
            <Expandable defaultOpened={false} title="Advanced Options">
              <Grid.Row>
                <MTUStatus
                  zoneId={zoneId}
                  value={formData.mtu}
                  onChange={handleInputChange}
                />
                <Grid.Column
                  textAlign="left"
                  width={8}
                  className="margin-top-20 flex vbottom"
                >
                  <h5>Port Security</h5>
                </Grid.Column>

                <Grid.Column
                  textAlign="left"
                  width={8}
                  className="margin-top-20"
                >
                  <Checkbox
                    className="display-inlineblock"
                    name="portSecurity"
                    toggle
                    checked={formData.portSecurity}
                    label={formData.portSecurity ? "Enabled" : "Disabled"}
                    onChange={handleCheckboxChange}
                  />
                </Grid.Column>
              </Grid.Row>
            </Expandable>
            <Grid.Row className="separator padding-top-00"></Grid.Row>
          </>

          {/* SUBNETS */}
          <ConnectedSubnets network={selectedNetwork} />

          {/* Ports */}
          <ConnectedPorts network={selectedNetwork} />

          {/* ACTION */}
          <Grid.Row className="padding-top-00">
            <Grid.Column textAlign="left" width={8}>
              <button
                className="button button--bordered"
                onClick={closeSlidingMenuLayer}
              >
                <span>Back</span>
              </button>
            </Grid.Column>
            {isFormChanged && (
              <Grid.Column textAlign="left" width={8}>
                {(() => {
                  const baseClasses = "float-right button button--green";

                  if (error) {
                    return (
                      <Popup
                        trigger={
                          <button
                            className={`${baseClasses} button--disabled button--icon__left`}
                            onClick={onDisabledClick}
                          >
                            <Icon name="exclamation circle" />
                            <span>Update</span>
                          </button>
                        }
                      >
                        {error.message}
                      </Popup>
                    );
                  }

                  if (isModifying) {
                    return (
                      <button
                        className={`${baseClasses} overflow-hidden button--icon__right`}
                      >
                        <Icon loading name="spinner" />
                        <span>Updating</span>
                      </button>
                    );
                  }

                  return (
                    <button className={baseClasses} onClick={modifyAction}>
                      <span>Update</span>
                    </button>
                  );
                })()}
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      </div>
    </div>
  );
};

export default ModifyNetwork;
